@if (showLoading$ | async) {
  <app-step-loading></app-step-loading>
} @else if (inputData$ | async; as inputData) {
  <div class="max-w-xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 p-1">
    <div class="text-center space-y-2 mb-8">
      <h2 class="text-2xl font-bold text-gray-800">What are you teaching?</h2>
      <p class="text-gray-500">Enter a topic, standard, or description of your lesson.</p>
    </div>

    <div class="bg-white p-6 md:p-8 rounded-2xl shadow-lg shadow-gray-100 border border-gray-100 space-y-6">
      <!-- Topic Input -->
      <div>
        <label class="block text-sm font-bold text-gray-700 mb-1">Lesson Topic</label>
        <p class="text-xs text-gray-500 mb-2">Be as specific as you'd like — the more detail, the better the results.</p>
        <div class="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
          <!-- Textarea with lightbulb icon -->
          <div class="relative">
            <div class="absolute top-3 left-3 text-gray-400">
              <lucide-icon [img]="icons.Lightbulb" class="w-5 h-5" [style.color]="themeColor"></lucide-icon>
            </div>
            <textarea
              placeholder="Enter your topic, standard, or a longer description..."
              class="w-full pl-11 pr-4 pt-3 pb-2 text-gray-800 text-base resize-y min-h-[180px] outline-none border-none bg-transparent"
              [ngModel]="inputData.topic"
              (ngModelChange)="updateTopic($event)"
              rows="4"
            ></textarea>
            <!-- Helper text inside textarea area -->
            @if (!inputData.topic) {
              <div class="px-11 pb-3 text-xs text-gray-400 leading-relaxed pointer-events-none">
                Examples: "Photosynthesis", "Fractions and decimals", "Theme in literature", "The American Revolution"
              </div>
            }
          </div>

          <!-- Uploaded Files List -->
          @if (uploadedFiles$ | async; as files) {
            @if (files.length > 0) {
              <div class="px-3 pb-2 space-y-2">
                @for (file of files; track file.name) {
                  <div
                    class="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-100"
                  >
                    <div class="flex items-center gap-2 min-w-0">
                      <lucide-icon [img]="icons.FileText" class="w-4 h-4 text-gray-500 flex-shrink-0"></lucide-icon>
                      <div class="min-w-0">
                        <p class="text-sm font-medium text-gray-700 truncate">{{ file.name }}</p>
                        <p class="text-xs text-gray-400">{{ formatFileSize(file.size) }}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 cursor-pointer"
                      (click)="removeFile(file)"
                    >
                      <lucide-icon [img]="icons.X" class="w-4 h-4 text-gray-500"></lucide-icon>
                    </button>
                  </div>
                }
              </div>
            }
          }

          <!-- Bottom bar with Add File -->
          <div class="flex items-center px-3 py-2 border-t border-gray-100">
            <div class="relative">
              <input
                #fileInput
                type="file"
                class="hidden"
                [accept]="acceptedFileTypes"
                multiple
                (change)="onFileSelect($event)"
              />
              <button
                type="button"
                class="flex items-center gap-1.5 text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity"
                [style.color]="themeColor"
                (click)="triggerFileInput()"
              >
                <lucide-icon [img]="icons.FilePlus2" class="w-4 h-4"></lucide-icon>
                <span>Add File</span>
                <lucide-icon [img]="icons.ChevronDown" class="w-3.5 h-3.5"></lucide-icon>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      @if (error$ | async; as error) {
        <div class="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {{ error }}
        </div>
      }
    </div>

    <!-- Action Buttons -->
    <div class="flex gap-3 pt-4 pb-8">
      <button
        (click)="onBack()"
        class="flex-1 py-3 text-gray-500 font-medium bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
      >
        Back
      </button>
      <button
        (click)="onGenerateObjectives()"
        [disabled]="!inputData.topic"
        class="flex-[2] py-3 text-white font-bold rounded-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        [style.backgroundColor]="themeColor"
      >
        Generate Objectives
      </button>
    </div>
  </div>
}
